import React, { useEffect, useMemo, useState } from 'react';
import { Button, SimpleGrid, Stack, useToast } from '@chakra-ui/react';
import { ArrowLeft, Save } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { getCatalogue, getCatalogueItems } from '../../hooks/catalogueItems';
import { addOrderItem, createOrder } from '../../hooks/orders';
import { fetchLeads } from '../../hooks/leads';
import { Lead, OrderItem } from '../../utils/types';
import { LeadSelector } from '../quotes/components/LeadSelector';
import {
  OrderLineItemsSection,
  OrderMetaSection,
  OrderRecipientSection,
  OrderSummaryRail,
  createDraftOrderItem,
} from './components/orderBuilderShared';
import {
  OrderHero,
  OrderPrimaryButton,
  OrderSectionCard,
  OrderShell,
} from './components/orderUi';

const normalizeLeads = (data: unknown): Lead[] => {
  if (Array.isArray(data)) {
    return data as Lead[];
  }

  if (data && typeof data === 'object' && Array.isArray((data as { results?: unknown[] }).results)) {
    return (data as { results: Lead[] }).results;
  }

  return [];
};

const normalizeProducts = (data: unknown) => {
  if (Array.isArray(data)) {
    return data as Array<{ id: string; name: string; price?: number }>;
  }

  if (data && typeof data === 'object' && Array.isArray((data as { results?: unknown[] }).results)) {
    return (data as { results: Array<{ id: string; name: string; price?: number }> }).results;
  }

  return [];
};

type OrderFormState = {
  source: string;
  status: string;
  payment_status: string;
  fulfillment_status: string;
  priority: string;
  notes: string;
  internal_notes: string;
  delivery_method: string;
  delivery_address: string;
  delivery_notes: string;
  requested_delivery_date: string;
};

const initialFormState: OrderFormState = {
  source: 'wa',
  status: 'draft',
  payment_status: 'unpaid',
  fulfillment_status: 'unfulfilled',
  priority: 'normal',
  notes: '',
  internal_notes: '',
  delivery_method: 'pickup',
  delivery_address: '',
  delivery_notes: '',
  requested_delivery_date: '',
};

export default function CreateOrderPage() {
  const { orgId, catalogueId } = useParams<{ orgId: string; catalogueId?: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const [leads, setLeads] = useState<Lead[]>([]);
  const [products, setProducts] = useState<Array<{ id: string; name: string; price?: number }>>([]);
  const [selectedLeadId, setSelectedLeadId] = useState('');
  const [items, setItems] = useState<OrderItem[]>([]);
  const [form, setForm] = useState<OrderFormState>(initialFormState);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        const leadResponsePromise = fetchLeads(orgId || '');
        const resolvedCatalogueId = catalogueId
          ? catalogueId
          : ((await getCatalogue(orgId || ''))?.data?.id as string | undefined);
        const productResponsePromise = resolvedCatalogueId
          ? getCatalogueItems(orgId || '', String(resolvedCatalogueId))
          : Promise.resolve({ success: true, data: [] });

        const [leadResponse, productResponse] = await Promise.all([
          leadResponsePromise,
          productResponsePromise,
        ]);

        if (!mounted) {
          return;
        }

        if (leadResponse?.success) {
          setLeads(normalizeLeads(leadResponse.data));
        }

        if (productResponse?.success) {
          setProducts(normalizeProducts(productResponse.data));
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [catalogueId, orgId]);

  const selectedLead = useMemo(
    () => leads.find((lead) => String(lead.id) === String(selectedLeadId)) || null,
    [leads, selectedLeadId],
  );

  const handleChangeForm = (field: string, value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleAddItem = () => {
    setItems((current) => [...current, createDraftOrderItem()]);
  };

  const handleDuplicateItem = (index: number) => {
    setItems((current) => {
      const item = current[index];
      if (!item) {
        return current;
      }

      const duplicate = {
        ...item,
        ...createDraftOrderItem(),
        product_name: item.product_name,
        description: item.description,
        sku: item.sku,
        quantity: item.quantity,
        unit_price: Number(item.unit_price ?? item.price ?? 0),
        total_price:
          Number(item.quantity || 0) * Number(item.unit_price ?? item.price ?? 0),
      };

      const next = [...current];
      next.splice(index + 1, 0, duplicate);
      return next;
    });
  };

  const handleChangeItem = (
    index: number,
    field: keyof OrderItem,
    value: string | number,
  ) => {
    setItems((current) => {
      const next = [...current];
      const item = next[index];
      if (!item) {
        return current;
      }

      const updated: OrderItem = { ...item, [field]: value } as OrderItem;

      if (field === 'product_name') {
        const selectedProduct = products.find((product) => product.name === value);
        updated.unit_price = Number(selectedProduct?.price || updated.unit_price || 0);
      }

      updated.total_price =
        Number(updated.quantity || 0) * Number(updated.unit_price ?? updated.price ?? 0);
      updated.price = Number(updated.unit_price ?? updated.price ?? 0);
      next[index] = updated;
      return next;
    });
  };

  const handleRemoveItem = (index: number) => {
    setItems((current) => current.filter((_, itemIndex) => itemIndex !== index));
  };

  const handleCreate = async () => {
    if (!orgId || !selectedLeadId || items.length === 0) {
      toast({
        title: 'Finish the essentials first',
        description: 'Select a customer and add at least one line before saving the order.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const validItems = items.filter((item) => item.product_name.trim());
    if (validItems.length === 0) {
      toast({
        title: 'Add at least one item',
        description: 'Each order needs at least one product or service line before it can be saved.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        lead_id: selectedLeadId,
        source: form.source,
        status: form.status,
        payment_status: form.payment_status,
        fulfillment_status: form.fulfillment_status,
        priority: form.priority,
        notes: form.notes,
        internal_notes: form.internal_notes,
        delivery_method: form.delivery_method,
        delivery_address: form.delivery_address,
        delivery_notes: form.delivery_notes,
        requested_delivery_date: form.requested_delivery_date || null,
      };

      const orderResponse = await createOrder(orgId, payload);
      if (!orderResponse.success || !orderResponse.data || typeof orderResponse.data !== 'object') {
        throw new Error('Order creation failed');
      }

      const createdOrderId = (orderResponse.data as { id?: string }).id;
      if (!createdOrderId) {
        throw new Error('Missing order id');
      }

      for (const item of validItems) {
        const itemResponse = await addOrderItem(orgId, createdOrderId, {
          product_name: item.product_name,
          description: item.description || '',
          sku: item.sku || '',
          quantity: Number(item.quantity || 1),
          unit_price: Number(item.unit_price ?? item.price ?? 0),
        });

        if (!itemResponse.success) {
          throw new Error('Item creation failed');
        }
      }

      toast({
        title: 'Order created',
        description: 'The order is ready for review, fulfilment, and follow-up.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      navigate(`/organization/${orgId}/orders/${createdOrderId}`);
    } catch (error) {
      toast({
        title: 'Could not create order',
        description: 'Please review the details and try again.',
        status: 'error',
        duration: 3500,
        isClosable: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <OrderShell compact>
        <OrderSectionCard
          title="Loading order builder"
          description="Please wait while customers and catalogue items are loading."
        >
          <Button variant="ghost" isLoading loadingText="Loading" />
        </OrderSectionCard>
      </OrderShell>
    );
  }

  return (
    <OrderShell compact>
      <OrderHero
        eyebrow="New order"
        title="Create an order with clear customer context and live totals"
        description="Start with the customer, add the right items, and keep payment and delivery details visible as you go."
        primaryAction={
          <OrderPrimaryButton
            leftIcon={<Save size={16} />}
            onClick={handleCreate}
            isLoading={submitting}
            loadingText="Saving"
          >
            Save order
          </OrderPrimaryButton>
        }
        secondaryAction={
          <Button borderRadius="16px" variant="outline" leftIcon={<ArrowLeft size={16} />} onClick={() => navigate(-1)}>
            Back
          </Button>
        }
      />

      <SimpleGrid columns={{ base: 1, xl: 3 }} spacing={6} alignItems="start">
        <Stack spacing={6} gridColumn={{ xl: 'span 2' }}>
          <OrderRecipientSection
            selectedLeadId={selectedLeadId}
            selectedLead={selectedLead}
            selector={
              <LeadSelector
                orgId={orgId || ''}
                selectedLead={selectedLeadId}
                onSelect={setSelectedLeadId}
                onAddNew={() => navigate(`/organization/${orgId}/leads/new`)}
              />
            }
          />

          <OrderLineItemsSection
            items={items}
            products={products}
            onAddItem={handleAddItem}
            onDuplicateItem={handleDuplicateItem}
            onChangeItem={handleChangeItem}
            onRemoveItem={handleRemoveItem}
          />

          <OrderMetaSection
            source={form.source}
            status={form.status}
            paymentStatus={form.payment_status}
            fulfillmentStatus={form.fulfillment_status}
            priority={form.priority}
            requestedDeliveryDate={form.requested_delivery_date}
            deliveryMethod={form.delivery_method}
            notes={form.notes}
            internalNotes={form.internal_notes}
            deliveryAddress={form.delivery_address}
            deliveryNotes={form.delivery_notes}
            onChange={handleChangeForm}
          />
        </Stack>

        <Stack spacing={6}>
          <OrderSummaryRail
            reference={undefined}
            status={form.status}
            paymentStatus={form.payment_status}
            fulfillmentStatus={form.fulfillment_status}
            items={items}
            requestedDeliveryDate={form.requested_delivery_date}
            source={form.source}
            priority={form.priority}
          />
          <OrderSectionCard
            title="Actions"
            description="Keep the main actions close while you build the order."
          >
            <Stack spacing={3}>
              <OrderPrimaryButton
                leftIcon={<Save size={16} />}
                onClick={handleCreate}
                isLoading={submitting}
                loadingText="Saving"
              >
                Save order
              </OrderPrimaryButton>
              <Button borderRadius="16px" variant="outline" onClick={() => navigate(`/organization/${orgId}/orders`)}>
                Back to orders
              </Button>
            </Stack>
          </OrderSectionCard>
        </Stack>
      </SimpleGrid>
    </OrderShell>
  );
}
